"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MbAuthoriser = exports.DEFAULT_STATE = void 0;
// Events
const events_1 = require("../../security/events");
const challenges_constants_1 = require("../../security/authoriser/constants/challenges.constants");
// Helpers
const authoriser_helper_1 = require("../../security/authoriser/helpers/authoriser.helper");
const challengeUtils_1 = require("../../security/authoriser/utils/challengeUtils");
// Types
const types_1 = require("../../security/authoriser/types");
const activeAsyncConnection_1 = require("../../activeAsyncConnection");
// IVR
const ivrModel_1 = require("../../security/authoriser/domain/models/ivrModel");
const events_2 = require("../../security/events");
const DEFAULT_CHALLENGE_STATE = {
    isChallengeLoading: false,
    isChallengeBlocked: false,
    isChallengeVerifying: false,
    challengeVerificationError: null,
    isChallengeVerificationSucceeded: false,
    isChallengeVerificationFailed: false,
    challengeRetryStatus: types_1.ChallengeRetryStatus.DEFAULT,
    ivrChallengeStatus: null,
};
exports.DEFAULT_STATE = {
    isStarted: false,
    isFailed: false,
    isBlocked: false,
    isReadyToFinish: false,
    isFinishing: false,
    isFinished: false,
    error: null,
    ...DEFAULT_CHALLENGE_STATE,
};
class MbAuthoriser {
    constructor(asyncConnectionMode = activeAsyncConnection_1.AsyncConnectionMode.WEBSOCKET) {
        this.challenge = null;
        this.pastChallenges = [];
        this.state = exports.DEFAULT_STATE;
        this.progressId = null;
        this.asyncConnectionMode = asyncConnectionMode;
    }
    setChallengeFactory(challengeFactory) {
        this.challengeFactory = challengeFactory;
    }
    initializeAuthoriser(initData, originalRequest, url) {
        this.accessToken = initData.accessToken;
        const firstChallengeData = (0, challengeUtils_1.mapToChallenge)(initData);
        if (url && url !== '') {
            this.requestId = (0, authoriser_helper_1.hashRequestURL)(url);
        }
        this.setChallenge(firstChallengeData);
        return (0, events_1.publishInitialiseAuthoriserEvent)({ firstChallenge: firstChallengeData, originalRequest });
    }
    onInitAuthoriser(callback) {
        return (0, events_1.subscribeInitAuthoriserEvent)(callback);
    }
    updateState(state) {
        this.state = {
            ...this.state,
            ...state,
        };
        (0, events_1.publishAuthoriserStateChangedEvent)(this.state);
    }
    onStateChange(callback) {
        return (0, events_1.subscribeAuthoriserStateChangedEvent)(callback);
    }
    async setChallenge(data) {
        if (this.challenge) {
            this.challenge.finish?.();
            this.pastChallenges.push(this.challenge);
            this.updateState({ isChallengeVerificationSucceeded: false, isChallengeVerificationFailed: false, challengeVerificationError: null });
        }
        if (data.type === types_1.ChallengeType.NONE) {
            this.challenge = null;
            this.updateState({ isFinished: true, isReadyToFinish: true });
            return;
        }
        this.challenge = this.challengeFactory.getChallenge(data);
        this.progressId = data.progressId;
        if (!this.challenge) {
            this.updateState({ isFailed: true });
            return;
        }
        this.updateState({ isChallengeLoading: true, isStarted: true });
        await this.challenge.init?.(this);
        this.updateState({ isChallengeLoading: false });
    }
    get challenges() {
        return [...this.pastChallenges, this.challenge];
    }
    getChallengeData() {
        if (this.state.isChallengeLoading) {
            return null;
        }
        return this.challenge?.data || null;
    }
    getChallengesData() {
        return this.challenges.map(challengeInstance => challengeInstance?.data || null);
    }
    async verifyChallenge(value) {
        if (!this.challenge || this.challenge?.data.type === types_1.ChallengeType.NONE) {
            return;
        }
        this.updateState({ isChallengeVerifying: true, isChallengeVerificationFailed: false });
        try {
            const nextChallengeData = await this.challenge.doVerify(value);
            this.updateState({ isChallengeVerificationSucceeded: true });
            this.setChallenge(nextChallengeData);
        }
        catch (error) {
            if (error.message !== challenges_constants_1.TRUSTED_VERIFICATION_INCOMPLETE) {
                this.updateState({ isChallengeVerificationFailed: true, challengeVerificationError: error });
            }
        }
        finally {
            this.updateState({ isChallengeVerifying: false });
        }
    }
    async retryChallenge() {
        const status = await this.challenge?.doRetry?.();
        if (status && status !== types_1.ChallengeRetryStatus.DEFAULT) {
            this.fail(status === types_1.ChallengeRetryStatus.LIMIT_EXCEEDED);
            this.updateState({ isChallengeBlocked: true, challengeRetryStatus: status });
        }
    }
    fail(block = false, error) {
        this.updateState({ isFailed: true, isBlocked: block, isReadyToFinish: false, error });
    }
    transferToChallengeIvr() {
        this.updateState({ isChallengeLoading: true });
        this.challenge.doTransfer();
    }
    subscribeIvrEvents() {
        const subscribers = {
            otpSuccessSubscriber: (callback) => (0, events_2.subscribeRaiseN2OTPIVRSuccessEvent)(callback),
            otpErrorSubscriber: (callback) => (0, events_2.subscribeRaiseN2OTPIVRErrorEvent)(callback),
            skSuccessSubscriber: (callback) => (0, events_2.subscribeRaiseN2IVRSuccessEvent)(callback),
            skErrorSubscriber: (callback) => (0, events_2.subscribeRaiseN2IVRErrorEvent)(callback),
        };
        // TODO: Challenge must be finished automatically, so agent should continue with operation
        subscribers.otpSuccessSubscriber((data) => {
            this.updateState({ ivrChallengeStatus: ivrModel_1.IVR_SUCCESS.SUCCESS });
            this.verifyChallenge(data);
            this.updateState({ isChallengeLoading: false });
        });
        subscribers.otpErrorSubscriber((error) => {
            this.updateState({ isChallengeLoading: false, ivrChallengeStatus: error });
        });
        subscribers.skSuccessSubscriber((data) => {
            this.updateState({ ivrChallengeStatus: ivrModel_1.IVR_SUCCESS.SUCCESS });
            this.verifyChallenge(data);
            this.updateState({ isChallengeLoading: false });
        });
        subscribers.skErrorSubscriber((error) => {
            this.updateState({ isChallengeLoading: false, ivrChallengeStatus: error });
        });
    }
    unsubscribeIvrEvents() {
        // TODO
    }
    reset() {
        this.pastChallenges = [];
        this.requestId = null;
        const canRetryChallenge = this.state.isFailed && !this.state.isBlocked;
        if (canRetryChallenge) {
            this.updateState({
                isFailed: false,
                isBlocked: false,
                isChallengeBlocked: false,
                challengeRetryStatus: types_1.ChallengeRetryStatus.DEFAULT,
                isChallengeVerificationFailed: false,
                isChallengeVerificationSucceeded: false,
                ivrChallengeStatus: null,
            });
            return;
        }
        this.challenge?.finish?.();
        this.challenge = null;
        this.progressId = null;
        this.accessToken = undefined;
        this.updateState(exports.DEFAULT_STATE);
    }
}
exports.MbAuthoriser = MbAuthoriser;
//# sourceMappingURL=authoriser.js.map